/**
 * The strip that tells an engineer whether their evidence is safe.
 *
 * Everything on this app is recoverable except one thing: photos that exist only
 * in this phone's storage. iOS evicts site data from apps it thinks are idle,
 * and it does not ask first. By the time an engineer notices, the switchroom
 * visit is over.
 *
 * So this is deliberately intrusive at the top end and silent at the bottom. A
 * banner that is always on screen stops being read, and then it is not read on
 * the day it matters.
 */

import { memo } from 'react';
import { pressureOf } from '../capture/photo.js';
import { installAdvice, syncPromise, type Platform } from '../platform/ios.js';

export interface DeviceStatusProps {
  platform: Platform;
  /** Bytes of photos held locally and not yet uploaded. */
  bytesHeld: number;
  /** How many photos are waiting. */
  pendingPhotos: number;
  online: boolean;
  /** Runs the upload queue now. Absent while one is already running. */
  onSyncNow?: (() => void) | undefined;
  /** Lets the engineer put the install steps away; it is advice, not a gate. */
  onDismissInstall?: (() => void) | undefined;
  installDismissed?: boolean | undefined;
}

const mb = (bytes: number) => `${Math.round(bytes / (1024 * 1024))} MB`;

export const DeviceStatus = memo(function DeviceStatus({
  platform,
  bytesHeld,
  pendingPhotos,
  online,
  onSyncNow,
  onDismissInstall,
  installDismissed,
}: DeviceStatusProps) {
  const pressure = pressureOf(bytesHeld, pendingPhotos);
  const advice = installAdvice(platform);
  const showInstall = advice.show && !installDismissed;

  // An engineer offline with photos held is not in trouble — that is the normal
  // state in a switchroom. Being offline is only worth saying alongside the
  // pressure, which is why it is not a banner of its own.
  const nothingToSay = pressure.level === 'fine' && !showInstall && pendingPhotos === 0;
  if (nothingToSay) return null;

  return (
    <div className="device-status" data-testid="device-status">
      {showInstall ? (
        <section
          className="install-hint"
          data-testid="install-hint"
          aria-labelledby="install-hint-heading"
        >
          <h3 id="install-hint-heading">{advice.headline}</h3>
          {/* Installed, the app gets a larger storage quota and is evicted far
              less readily. On this app that is the difference between a day's
              photos surviving a night with no signal and not. */}
          <p>Installed, this phone keeps far more photos safely while you are out of signal.</p>
          <ol>
            {advice.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
          {onDismissInstall ? (
            <button type="button" onClick={onDismissInstall}>
              Not now
            </button>
          ) : null}
        </section>
      ) : null}

      {pendingPhotos > 0 ? (
        <section
          className={`pressure ${pressure.level}`}
          data-testid="pressure"
          data-level={pressure.level}
          // 'urgent' interrupts; the lower levels wait their turn, because a
          // polite warning read two seconds late costs nothing.
          role={pressure.level === 'urgent' ? 'alert' : 'status'}
        >
          <p data-testid="pressure-message">
            {pressure.message ??
              `${pendingPhotos} photo${pendingPhotos === 1 ? '' : 's'} (${mb(bytesHeld)}) waiting to upload.`}
          </p>
          {/* Said here rather than once at startup: this is the moment the
              promise matters, and on iOS the honest promise is a warning. */}
          <p data-testid="sync-promise">{syncPromise(platform)}</p>
          {online ? (
            onSyncNow ? (
              <button type="button" onClick={onSyncNow}>
                Upload now
              </button>
            ) : (
              <span data-testid="syncing">Uploading…</span>
            )
          ) : (
            <span data-testid="offline">No signal — they will upload when you have one.</span>
          )}
        </section>
      ) : null}
    </div>
  );
});
